import { isAfter, startOfDay } from 'date-fns'
import moment from 'moment'
import { string, func, bool, object, instanceOf, oneOf } from 'prop-types'
import { useCallback } from 'react'
import {
  kuiperClassNames,
  InputCollection,
  Tabs,
  Button,
  DatePickerPopover,
  Tooltip,
} from '@opallabs/kuiper'
import TimeInput from '../TimeInput'
import { TimeZoneSelect } from './TimeZoneSelect'
import './styles.scss'

export const getCollectionValues = ({ date, time, targetZone }) => ({
  dateInput: date,
  timeInput: time,
  timezoneSelect: targetZone,
})

const meridiems = ['am', 'pm']

const amOrPm = (meridiem) => meridiems.indexOf(meridiem)

const renderDateInput = ({
  allowableDates,
  contextDateTime,
  date,
  dateEditable,
  placeholder = 'No Date Selected',
  range,
  tooltipText,
}) => {
  let providedDate = date
  if (!providedDate && contextDateTime) {
    providedDate = moment(contextDateTime)
  }

  const dateInput = (
    <DatePickerPopover
      classNames={['date-input', `${dateEditable ? '' : 'date-uneditable'}`]}
      name="dateInput"
      placeholder={placeholder}
      disabled={!dateEditable}
      popoverOptions={{
        constraints: [{ popover: 'bottom left', attachment: 'top left' }],
      }}
      range={range}
      allowableDates={allowableDates}
      icon="gem-calendar"
      iconPosition="left"
      date={providedDate}
      dateFormat="MMMM D, YYYY"
    />
  )

  if (tooltipText && !dateEditable) {
    return (
      <Tooltip
        className={kuiperClassNames(
          'background-hoverable-info',
          'date-tooltip-message'
        )}
        name="dateInputTooltip"
        position="top"
        trigger={dateInput}
        content={tooltipText}
      />
    )
  } else {
    return dateInput
  }
}

const renderTimezone = ({
  contextDateTime,
  locale,
  realDate,
  targetZone,
  timezoneDisabled,
}) => (
  <TimeZoneSelect
    date={realDate || contextDateTime || new Date()}
    disabled={timezoneDisabled}
    locale={locale}
    name="timezoneSelect"
    selectedZone={targetZone}
  />
)

const renderTime = (timeProps) => {
  const {
    contextDateTime,
    hasTime,
    locale,
    meridiem,
    onTimeInputBlur,
    realDate,
    showTimezone,
    targetZone,
    timezoneDisabled,
  } = timeProps
  if (hasTime) {
    return (
      <div name="wrapper">
        <TimeInput
          onBlur={onTimeInputBlur}
          placeholder="HH:MM"
          name="timeInput"
        />
        <Tabs
          name="meridiemSwitch"
          activeTab={amOrPm(meridiem)}
          theme="switch"
          {...timeProps}
        >
          <Button label="am" name="am" type="invisible" />
          <Button label="pm" name="pm" type="invisible" />
        </Tabs>
        {showTimezone
          ? renderTimezone({
              contextDateTime,
              locale,
              realDate,
              targetZone,
              timezoneDisabled,
            })
          : null}
      </div>
    )
  }

  return null
}

export const DateAndTime = ({
  allowableDates,
  contextDateTime,
  date,
  dateEditable = true,
  dateTooltipText = null,
  dangerValidation = false,
  hasDate = true,
  hasTime = true,
  label = 'Date And Time',
  locale,
  meridiem = 'am',
  onChange,
  onTimeInputBlur,
  placeholder = 'No Date Selected',
  range = false,
  realDate,
  showTimezone = true,
  shouldValidate = false,
  targetZone,
  theme,
  time,
  timezoneDisabled = false,
  valid = false,
}) => {
  const onChangeWithFallbackHandling = useCallback(
    (values) => {
      const { dateInput, timeInput } = values

      if (!dateInput) {
        const due_date = moment(contextDateTime)
        // match the goofy structure produced by Kuiper `<DatePickerPopover>`
        values.dateInput = {
          due_date,
          due_date_raw: due_date.format('Y-MM-DD'),
        }
      } else if (!timeInput || timeInput.length === 0) {
        const currentDate = dateInput.due_date_raw
          ? moment(dateInput.due_date_raw)
          : dateInput

        if (isAfter(startOfDay(currentDate.toDate()), contextDateTime)) {
          values.timeInput = '12:00'
        }
      }

      onChange(values)
    },
    [contextDateTime, onChange]
  )

  return (
    <InputCollection
      className={`date-and-time '${theme}`}
      toggleable={false}
      onChange={onChangeWithFallbackHandling}
      dangerValidation={dangerValidation}
      collectionHeader={label}
      shouldValidate={shouldValidate}
      valid={valid}
      value={getCollectionValues({ date, time, targetZone })}
    >
      {hasDate
        ? renderDateInput({
            allowableDates,
            contextDateTime,
            date,
            dateEditable,
            placeholder,
            range,
            tooltipText: dateTooltipText,
          })
        : null}
      {renderTime({
        contextDateTime,
        hasTime,
        locale,
        meridiem,
        onChange,
        onTimeInputBlur,
        realDate,
        targetZone,
        timezoneDisabled,
        showTimezone,
      })}
    </InputCollection>
  )
}
DateAndTime.displayName = 'Photon: DateAndTime'

DateAndTime.propTypes = {
  allowableDates: object,
  /**
   * A `Date` that can be used as a fallback if `realDate` is `undefined`.
   */
  contextDateTime: instanceOf(Date),
  dangerValidation: bool,
  /**
   * @type {import('moment').Moment | undefined}
   *
   * @deprecated Use `realDate` instead.
   */
  date: object,
  /**
   * @note Only set to `true` for post options.
   */
  dateEditable: bool,
  dateTooltipText: string,
  hasDate: bool,
  hasTime: bool,
  label: string,
  /**
   * @type {import('@opallabs/concepts').IETFLanguageTag | undefined}
   */
  locale: string,
  meridiem: string,
  onChange: func,
  onTimeInputBlur: func,
  placeholder: string,
  range: bool,
  realDate: instanceOf(Date),
  shouldValidate: bool,
  /**
   * @note Only set to `false` for post options.
   */
  showTimezone: bool,
  /**
   * @type {import('@opallabs/concepts').IANATimezoneIdentifier | undefined}
   */
  targetZone: string,
  theme: oneOf(['dark', 'light', 'invisible']),
  time: string,
  /**
   * @note Only set to `false` when displaying the end date in
   *   `<PostScheduler>`.
   */
  timezoneDisabled: bool,
  valid: bool,
}
